// src/App.tsx
import React from "react";
import {BrowserRouter, Navigate, Route, Routes, useSearchParams} from "react-router-dom";
import OuterLayout from "./polaris-components/OuterLayout";
import AthleteLayout from "./polaris-components/AthleteLayout";
import AthleteAdminLayout from "./polaris-components/AthleteAdminLayout";
import {LoginPage} from "./polaris-components/LoginPage";
import {ForgotPasswordPage} from "./polaris-components/ForgotPasswordPage";
import {MfaVerifyPage} from "./polaris-components/MfaVerifyPage";
import LandingPage from "./polaris-components/LandingPage";
import SignupPage from "./polaris-components/SignupPage";
import AddMemberProfile from "./polaris-components/AddMemberPage";
import ProfileHeader from "./polaris-components/ProfileHeader";
import TeamGrid from "./polaris-components/TeamMemberGrid";
import AdminProfilePage from "./polaris-components/AdminProfilePage";
import TeamViewPage from "./polaris-components/TeamViewPage";
import AthleteProfile from "./polaris-components/AthleteProfile";
import AthleteHomePage from "./polaris-components/AthleteHomePage";
import AthleteProfileReadOnly from "./polaris-components/AthleteProfileReadOnly";
import MediaKitPublicPage from "./polaris-components/MediaKitPublicPage";
import CollegePlanningPage from "./polaris-components/CollegePlanningPage";
import CollegeDetailsPage from "./polaris-components/CollegeDetailsPage";
import ConnectPage from "./polaris-components/ConnectPage";
import TermsAndConditionsPage from "./polaris-components/TermsAndConditionsPage";
import PrivacyPolicyPage from "./polaris-components/PrivacyPolicyPage";
import DataAccountDeletionPage from "./polaris-components/DataAccountDeletionPage";
import "./styles/global.css";
import RequireAuth from "./polaris-components/RequireAuth";
import RequireAdmin from "./polaris-components/RequireAdmin";
import RequirePasswordChange from "./polaris-components/RequirePasswordChange";
import RequireProfile from "./polaris-components/RequireProfile";
import AddAthleteProfilePage from "./polaris-components/AddAthleteProfilePage";
import AthleteDirectoryPage from "./polaris-components/AthleteDirectoryPage";
import GoogleLoginCallback from "./polaris-components/GoogleLoginCallback";
import CleverLoginCallback from "./polaris-components/CleverLoginCallback";
import TokenRedirect from "./polaris-components/TokenRedirect";
import CalendarPage from "./polaris-components/CalendarPage";
import DrivePage from "./polaris-components/DrivePage";
import DocumentsPage from "./polaris-components/DocumentsPage";
import ResourcesPage from "./polaris-components/ResourcesPage";
import ResourceDetailPage from "./polaris-components/ResourceDetailPage";
import { isAthlete, isPolaris } from "./utils/auth";
import NilEduPage from "./polaris-components/NilEduPage";
import RequirePolaris from "./polaris-components/RequirePolaris";
import PolarisLayout from "./polaris-components/PolarisLayout";
import PolarisOverviewPage from "./polaris-components/PolarisOverviewPage";
import PolarisUsersPage from "./polaris-components/PolarisUsersPage";
import PolarisMetricsPage, { PolarisMetricsOverviewPanel } from "./polaris-components/PolarisMetricsPage";
import PolarisSalesPage from "./polaris-components/PolarisSalesPage";
import PolarisSalesLeadsPanel from "./polaris-components/PolarisSalesLeadsPanel";
import PolarisProvisionOrganizationPage from "./polaris-components/PolarisProvisionOrganizationPage";
import PolarisCollegeDataPage from "./polaris-components/PolarisCollegeDataPage";
import PolarisCourseInsightsPage from "./polaris-components/PolarisCourseInsightsPage";
import PolarisReferenceDataPage from "./polaris-components/PolarisReferenceDataPage";
import PolarisHubContentPage from "./polaris-components/PolarisHubContentPage";
import InterestsManagementPanel from "./polaris-components/InterestsManagementPanel";
import SportsManagementPanel from "./polaris-components/SportsManagementPanel";
import PolarisCacheManagementPage from "./polaris-components/PolarisCacheManagementPage";
import PolarisFeatureIdeasPage from "./polaris-components/PolarisFeatureIdeasPage";
import PolarisSupportPage from "./polaris-components/PolarisSupportPage";
import FeatureRequestsPage from "./polaris-components/FeatureRequestsPage";
import CompanySupportPage from "./polaris-components/CompanySupportPage";
import GettingStartedPage from "./polaris-components/GettingStartedPage";
import GettingStartedArticlePage from "./polaris-components/GettingStartedArticlePage";
import GamePlanPage from "./polaris-components/GamePlanPage";
import DiscoverUPage from "./polaris-components/DiscoverUPage";
import TutoringPage from "./polaris-components/TutoringPage";
import SpotlightPage from "./polaris-components/SpotlightPage";
import AdvisingPage from "./polaris-components/AdvisingPage";
import SessionsPage from "./polaris-components/SessionsPage";
import { StripeProvider } from "./polaris-components/StripeProvider";
import { SubscriptionDashboard } from "./polaris-components/SubscriptionDashboard";
import { SubscriptionSuccess } from "./polaris-components/SubscriptionSuccess";
import { SubscriptionCancel } from "./polaris-components/SubscriptionCancel";
import { BillingPage } from "./polaris-components/BillingPage";
import CheckoutPage from "./polaris-components/CheckoutPage";
import CleverCheckoutPage from "./polaris-components/CleverCheckoutPage";
import SubscribeDetailsPage from "./polaris-components/SubscribeDetailsPage";
import CleverConfirmPage from "./polaris-components/CleverConfirmPage";
import { OnboardingWelcome } from "./polaris-components/OnboardingWelcome";
import { AuthProvider } from "./contexts/AuthContext";
import NoAthletesMessage from "./polaris-components/NoAthletesMessage";
import GuardianSelectPage from "./polaris-components/GuardianSelectPage";
import { isGuardian } from "./utils/auth";
import AboutPage from "./polaris-components/AboutPage";
import OurTeamPage from "./polaris-components/OurTeamPage";
import BlogListPage from "./polaris-components/BlogListPage";
import BlogDetailPage from "./polaris-components/BlogDetailPage";
import MarketingHubLayout from "./polaris-components/MarketingHubLayout";
import MarketingHubPage from "./polaris-components/MarketingHubPage";
import FamiliesPage from "./polaris-components/FamiliesPage";
import OrganizationsPage from "./polaris-components/OrganizationsPage";
import SchoolsPage from "./polaris-components/SchoolsPage";
import ContactPage from "./polaris-components/ContactPage";
import NewsletterPage from "./polaris-components/NewsletterPage";
import ProgramPage from "./polaris-components/ProgramPage";
import NcaaEligibilityTrackerPage from "./polaris-components/NcaaEligibilityTrackerPage";
import RagChat from "./polaris-components/rag/RagChat";
import RagAdmin from "./polaris-components/rag/RagAdmin";
import PolarisAssistAdminPage from "./polaris-components/PolarisAssistAdminPage";
import PolarisInstantChatAdminPage from "./polaris-components/PolarisInstantChatAdminPage";
import PolarisUserAiUsagePage from "./polaris-components/PolarisUserAiUsagePage";
import PolarisUserCollegeSearchesPage from "./polaris-components/PolarisUserCollegeSearchesPage";
import PolarisCompanyAiUsagePage from "./polaris-components/PolarisCompanyAiUsagePage";
import GoogleAnalyticsRouteTracker from "./components/GoogleAnalyticsRouteTracker";

// Component to handle role-based routing
function RoleBasedLayout() {
    if (isPolaris()) {
        return <Navigate to="/polaris-admin" replace />;
    }
    // Guardians viewing athletes should use AthleteLayout
    if (isAthlete() || isGuardian()) {
        return <AthleteLayout />;
    }
    return <OuterLayout />;
}

// Component to handle default redirects based on role
function DefaultRedirect() {
    if (isPolaris()) {
        return <Navigate to="/polaris-admin" replace />;
    }
    if (isAthlete() || isGuardian()) {
        return <Navigate to="/athlete/home" replace />;
    }
    return <Navigate to="/team" replace />;
}

/** Legacy /polaris-admin/provision bookmarks → Sales Provision tab (keeps query params). */
function RedirectToSalesProvision() {
    const [params] = useSearchParams();
    const qs = params.toString();
    return <Navigate to={`/polaris-admin/sales/provision${qs ? `?${qs}` : ""}`} replace />;
}

export default function App() {
    return (
        <div className="app-shell">
            <AuthProvider>
                <StripeProvider>
                    <BrowserRouter>
                        <GoogleAnalyticsRouteTracker />
                        <Routes>
                        {/* Public routes - NO authentication required */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/our-team" element={<OurTeamPage />} />
                        <Route path="/blogs" element={<BlogListPage />} />
                        <Route path="/blog/:slug" element={<BlogDetailPage />} />
                        <Route path="/marketinghub" element={<MarketingHubLayout />}>
                            <Route index element={<MarketingHubPage />} />
                            <Route path=":slug" element={<MarketingHubPage />} />
                        </Route>
                        <Route path="/families" element={<FamiliesPage />} />
                        <Route path="/organizations" element={<OrganizationsPage />} />
                        {/* Legacy path retired in redesign; kept as redirect for existing links */}
                        <Route path="/acadamies" element={<Navigate to="/organizations" replace />} />
                        <Route path="/schools" element={<SchoolsPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/contact-sales" element={<ContactPage type="sales" />} />
                        <Route path="/newsletter" element={<NewsletterPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/login/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/login/mfa" element={<MfaVerifyPage />} />
                        <Route path="/error" element={<LoginPage />} />
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/checkout/clever" element={<CleverCheckoutPage />} />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/subscribe/details" element={<SubscribeDetailsPage />} />
                        <Route path="/subscribe/clever-confirm" element={<CleverConfirmPage />} />
                        {/* Legal pages - PUBLIC */}
                        <Route path="/terms" element={<TermsAndConditionsPage />} />
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/data" element={<DataAccountDeletionPage />} />
                        {/* NEW: where backend redirects after Google login */}
                        <Route path="/login/callback" element={<GoogleLoginCallback />} />
                        {/* Clever SSO callback */}
                        <Route path="/auth/clever/callback" element={<CleverLoginCallback />} />
                        {/* Invitation token validation route - PUBLIC */}
                        <Route path="/auth/set-password" element={<TokenRedirect />} />
                        {/* Onboarding success page after Stripe checkout - PUBLIC */}
                        <Route path="/onboarding/welcome" element={<OnboardingWelcome />} />
                        {/* Subscription success page after Stripe payment - PUBLIC */}
                        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                        {/* Subscription cancel page if user cancels Stripe checkout - PUBLIC */}
                        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                        {/* Shared media kit - PUBLIC read-only access */}
                        <Route path="/athlete/:athleteName/media-kit/:uuid" element={<MediaKitPublicPage />} />
                        {/* Shared athlete profile - PUBLIC read-only access */}
                        <Route path="/athlete/:athleteName/:uuid" element={<AthleteProfileReadOnly />} />
                        {/* Guardian no athletes page */}
                        <Route path="/guardian/no-athletes" element={<NoAthletesMessage />} />

                        {/* Auth first so signed-in chrome (and the terms modal) never flash for guests */}
                        <Route element={<RequireAuth />}>
                            {/* Terms still render here; RequireAuth only waits for session hydration. */}
                            <Route path="/guardian/select" element={<GuardianSelectPage />} />
                            <Route element={<RoleBasedLayout />}>
                                <Route element={<RequirePasswordChange />}>
                                    <Route element={<RequireProfile />}>
                                    {/* Default redirect based on role */}
                                    <Route path="/dashboard" element={<DefaultRedirect />} />

                                    {/* Athlete-specific routes wrapped in AthleteAdminLayout */}
                                    <Route element={<AthleteAdminLayout />}>
                                        <Route path="/athlete/home" element={<AthleteHomePage />} />
                                        <Route path="/athlete/home/:athleteId" element={<AthleteHomePage />} />
                                        <Route path="/athlete/profile" element={<AthleteProfile />} />
                                        <Route path="/athlete/profile/:athleteId" element={<AthleteProfile />} />
                                        <Route path="/athlete/program" element={<ProgramPage />} />
                                        <Route path="/athlete/program/:athleteId" element={<ProgramPage />} />
                                        <Route path="/athlete/college-planning" element={<CollegePlanningPage />} />
                                        <Route path="/athlete/college-planning/:athleteId" element={<CollegePlanningPage />} />
                                        <Route path="/athlete/ncaa" element={<NcaaEligibilityTrackerPage />} />
                                        <Route path="/athlete/ncaa/:athleteId" element={<NcaaEligibilityTrackerPage />} />
                                        <Route path="/athlete/college-details/:collegeId" element={<CollegeDetailsPage />} />
                                        <Route path="/athlete/college-details/:collegeId/:athleteId" element={<CollegeDetailsPage />} />
                                        <Route path="/athlete/nil-edu" element={<NilEduPage />} />
                                        <Route path="/athlete/nil-edu/:athleteId" element={<NilEduPage />} />
                                        <Route path="/athlete/recruiting" element={<ConnectPage />} />
                                        <Route path="/athlete/recruiting/:athleteId" element={<ConnectPage />} />
                                        <Route path="/athlete/game-plan" element={<GamePlanPage />} />
                                        <Route path="/athlete/game-plan/:athleteId" element={<GamePlanPage />} />
                                        <Route path="/athlete/discover" element={<DiscoverUPage />} />
                                        <Route path="/athlete/discover/:athleteId" element={<DiscoverUPage />} />
                                        <Route path="/athlete/spotlight" element={<SpotlightPage />} />
                                        <Route path="/athlete/spotlight/:athleteId" element={<SpotlightPage />} />
                                        <Route path="/athlete/tutoring" element={<TutoringPage />} />
                                        <Route path="/athlete/tutoring/:athleteId" element={<TutoringPage />} />
                                        <Route path="/athlete/advising" element={<AdvisingPage />} />
                                        <Route path="/athlete/advising/:athleteId" element={<AdvisingPage />} />
                                        <Route path="/athlete/sessions" element={<SessionsPage />} />
                                        <Route path="/athlete/sessions/:athleteId" element={<SessionsPage />} />
                                        {/* Calendar and Drive routes - shared between athlete and admin views */}
                                        <Route path="/calendar" element={<CalendarPage />} />
                                        <Route path="/calendar/:athleteId" element={<CalendarPage />} />
                                        <Route path="/drive" element={<DrivePage />} />
                                        <Route path="/drive/:athleteId" element={<DrivePage />} />
                                        <Route path="/documents" element={<DocumentsPage />} />
                                        <Route path="/documents/:athleteId" element={<DocumentsPage />} />
                                    </Route>

                                {/* Admin/Staff routes */}
                                <Route path="/add-admin-profile" element={<AdminProfilePage />} />
                                <Route path="/add-member" element={<AddMemberProfile />} />
                                <Route path="/athletes/new" element={<AddAthleteProfilePage />} />
                                <Route path="/athletes/:id/edit" element={<AddAthleteProfilePage />} />
                                <Route path="/athletes-directory" element={<AthleteDirectoryPage />} />
                                <Route path="/admin-profile" element={<ProfileHeader />} />

                                {/* Polaris Assist — athletes, guardians, admin, and staff */}
                                <Route path="/ai-assistant" element={<RagChat />} />

                                {/* Team pages */}
                                <Route path="/team" element={<TeamGrid />} />
                                <Route path="/team/view/:userId" element={<TeamViewPage />} />
                                <Route path="/resources" element={<ResourcesPage />} />
                                <Route path="/resources/:slug" element={<ResourceDetailPage />} />
                                <Route path="/program" element={<ProgramPage />} />
                                <Route path="/community" element={<FeatureRequestsPage />} />
                                <Route path="/requests" element={<Navigate to="/community" replace />} />
                                <Route path="/support-tickets" element={<CompanySupportPage />} />
                                <Route path="/getting-started" element={<GettingStartedPage />} />
                                <Route path="/getting-started/:slug" element={<GettingStartedArticlePage />} />

                                 {/* Admin-only routes */}
                                <Route element={<RequireAdmin />}>
                                    <Route path="/billing" element={<BillingPage />} />
                                    <Route path="/subscription-dashboard" element={<SubscriptionDashboard />} />
                                    <Route path="/subscription-upgrade" element={<Navigate to="/billing" replace />} />
                                </Route>
                                    </Route> {/* Close RequireProfile */}
                                </Route>
                            </Route>
                            <Route path="*" element={<DefaultRedirect />} />
                        </Route>

                        {/* Polaris superadmin portal */}
                        <Route path="/polaris-admin" element={<RequirePolaris />}>
                            <Route element={<PolarisLayout />}>
                                <Route index element={<Navigate to="/polaris-admin/companies" replace />} />
                                <Route path="companies" element={<PolarisOverviewPage />} />
                                <Route path="companies/:companyId/ai-usage" element={<PolarisCompanyAiUsagePage />} />
                                <Route path="users" element={<PolarisUsersPage />} />
                                <Route path="users/:userId/ai-usage" element={<PolarisUserAiUsagePage />} />
                                <Route path="users/:userId/college-searches" element={<PolarisUserCollegeSearchesPage />} />
                                <Route path="ai-assistant" element={<PolarisAssistAdminPage />} />
                                <Route path="instant-chat" element={<PolarisInstantChatAdminPage />} />
                                <Route path="metrics" element={<PolarisMetricsPage />}>
                                    <Route index element={<PolarisMetricsOverviewPanel />} />
                                    <Route path="course-insights" element={<PolarisCourseInsightsPage />} />
                                </Route>
                                <Route path="course-insights" element={<Navigate to="/polaris-admin/metrics/course-insights" replace />} />
                                <Route path="sales" element={<PolarisSalesPage />}>
                                    <Route index element={<PolarisSalesLeadsPanel />} />
                                    <Route path="provision" element={<PolarisProvisionOrganizationPage />} />
                                </Route>
                                <Route path="provision" element={<RedirectToSalesProvision />} />
                                <Route path="college-data" element={<Navigate to="/polaris-admin/reference-data/college-data" replace />} />
                                <Route path="hub" element={<Navigate to="/polaris-admin/hub/videos" replace />} />
                                <Route path="hub/videos" element={<PolarisHubContentPage />} />
                                <Route path="hub/resources" element={<PolarisHubContentPage />} />
                                <Route path="reference-data" element={<PolarisReferenceDataPage />}>
                                    <Route index element={<Navigate to="interests" replace />} />
                                    <Route path="interests" element={<InterestsManagementPanel />} />
                                    <Route path="sports" element={<SportsManagementPanel />} />
                                    <Route path="college-data" element={<PolarisCollegeDataPage />} />
                                </Route>
                                <Route path="feature-ideas" element={<PolarisFeatureIdeasPage />} />
                                <Route path="support" element={<PolarisSupportPage />} />
                                <Route path="caches" element={<PolarisCacheManagementPage />} />
                                <Route path="knowledge-base" element={<RagAdmin />} />
                            </Route>
                        </Route>

                    </Routes>
                </BrowserRouter>
            </StripeProvider>
            </AuthProvider>
        </div>
    );
}
